import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Sparkle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { DomainPicker } from "@/components/DomainPicker";

export default function Paths() {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/paths").then((d) => {
      setPaths(d.paths || []);
      setLoading(false);
    });
  }, []);

  const generate = async () => {
    setBusy("gen");
    try {
      setPaths((await api.post("/paths/generate")).paths);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const pick = async (p) => {
    setBusy(p.path_id);
    try {
      await api.post("/roadmap/generate", { path_id: p.path_id });
      track("path_chosen", { title: p.title, domain: p.domain, unexpected: !!p.unexpected });
      toast.success(`Roadmap ready for ${p.title}`);
      navigate("/roadmap");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="overline" data-testid="paths-loading">Loading paths</p>;

  return (
    <div className="space-y-6" data-testid="paths-page">
      <div className="flex flex-wrap items-end gap-6 rise">
        <div>
          <p className="overline">Step 03 — Possibilities</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-none">Possibilities worth considering</h1>
          <p className="mt-5 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Careers, courses, apprenticeships, hobbies, projects, places to live, communities,
            skills, ways to make money. Some will be obvious. Two won't be.
          </p>
        </div>
        <button onClick={generate} disabled={busy === "gen"} data-testid="generate-paths-button"
          className="ml-auto flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/12 text-sm hover:bg-white/[0.06] transition-colors duration-200 disabled:opacity-40">
          <RefreshCw className={`h-4 w-4 ${busy === "gen" ? "animate-spin" : ""}`} />
          {paths.length ? "Regenerate" : "Generate paths"}
        </button>
      </div>

      <DomainPicker />

      {!paths.length && (
        <div className="panel p-12" data-testid="paths-empty">
          <p className="text-sm text-muted-foreground">No paths yet. Approve your profile, then generate.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {paths.map((p, i) => (
          <div key={p.path_id} data-testid={`path-card-${i}`}
            className={`panel p-8 rise flex flex-col hover:bg-white/[0.045] transition-colors duration-200 ${
              p.unexpected ? "border-accent/35" : ""
            }`}
            style={{ animationDelay: `${i * 80}ms` }}>
            <div className="flex items-start gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-primary/30 text-primary">
                    {p.domain || "career"}
                  </span>
                  <span className="overline">{p.archetype}</span>
                </div>
                <h2 className="mt-3 font-display text-2xl tracking-tight">{p.title}</h2>
              </div>
              <div className="ml-auto text-right shrink-0">
                <p className="font-display text-2xl text-primary">{p.fit_score}</p>
                <p className="overline">fit</p>
              </div>
            </div>

            {p.unexpected && (
              <span className="mt-5 self-start flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-accent/40 text-accent">
                <Sparkle className="h-3 w-3" /> You probably didn't consider this
              </span>
            )}

            <p className="mt-6 text-sm leading-relaxed">{p.why_you}</p>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{p.what_it_looks_like}</p>

            <div className="mt-7 grid sm:grid-cols-2 gap-6">
              <div>
                <span className="overline">Uses your</span>
                <ul className="mt-3 space-y-1.5">
                  {(p.strength_matches || []).map((s) => (
                    <li key={s} className="text-sm text-muted-foreground">— {s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="overline">You'd need</span>
                <ul className="mt-3 space-y-1.5">
                  {(p.gaps || []).map((s) => (
                    <li key={s} className="text-sm text-muted-foreground">— {s}</li>
                  ))}
                </ul>
              </div>
            </div>

            <button onClick={() => pick(p)} disabled={!!busy} data-testid={`choose-path-${i}`}
              className="mt-8 group flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors duration-200">
              {busy === p.path_id ? "Mapping it out…" : "Build my roadmap for this"}              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
